import { CONTENT_CARD, TEXT, CONTENT_PAGINATION_BUTTON, CONTENT_EMPTY_MESSAGE, CONTENT_LOADING, CONTENT_FILTER_CONTAINER, CAROUSEL_CARD, SECTION, MENU_CONTAINER, MENU_DRAGGABLE_CONTAINER, CONTENT, MENU, FORM_STEP, CAROUSEL, SEEK } from "constants/widgetTypes";
import { generateLayoutProperties, parseHtmlToDocument, parseInlineStyles } from "./utils/layoutUtils";
import { getParentContainerOfType } from "../helper/widget";
import { Frame } from "scenejs";
import _ from "lodash";

const ignoreTypes = [CONTENT_CARD, CONTENT_PAGINATION_BUTTON, CONTENT_EMPTY_MESSAGE, CONTENT_LOADING, CONTENT_FILTER_CONTAINER, CAROUSEL_CARD, MENU_CONTAINER, MENU_DRAGGABLE_CONTAINER, FORM_STEP];
const contentCardUids = [];
const ignoreHeightWidgets = [SECTION, CONTENT];
const fullWidthWidgets = [MENU, SECTION, CONTENT, CAROUSEL, SEEK];
const mobileWidth = 375;

export const generateLayoutChangesFromCritique = (editorSession, critiqueResponse, widgets) => {
  if (!critiqueResponse) return {};

  const layoutRequest = {};
  const steps = critiqueResponse.steps || [];
  widgets
    .filter(w => w.type === MENU_DRAGGABLE_CONTAINER)
    .forEach(widget => {
      layoutRequest[widget.uid] = {
        id: widget.uid,
        path: "properties.mobile.layout",
        value: calculateLeftOffsetForMenuContainer(widget)
      };
  });

  for (const step of steps) {
    const uid = step.widget_id;

    const widget = widgets.find(w => w.uid === uid);
    if (!widget) continue;

    const type = widget.type;
    const parent = widget.parentId;
    const isContentCardChild = getParentContainerOfType(editorSession, uid, CONTENT_CARD);

    if (ignoreTypes.includes(type) || isContentCardChild) {
      continue;
    }

    const recommendation = step.recommendation || {};
    const existingLayout = _.get(widget, `properties.mobile.layout`);
    const [existingX, existingY] =
      existingLayout?.transform?.translate
        ?.split(",")
        .map(v => v.trim()) || [];
    const top = recommendation.top ?? existingY ?? "0px";
    const left = fullWidthWidgets.includes(type) ? "0px" : recommendation.left ?? existingX ?? "0px";
    const width = recommendation.width ?? existingLayout.width;
    const height = recommendation.height ?? existingLayout.height;

    const frame = new Frame({
      ...existingLayout,
      transform: existingLayout.transform ?? {}
    });
    
    frame.set("transform", "translate", `${left}, ${top}`);
    
    const translate = `${left}, ${top}`;

    frame.set("transform", "translate", translate);
    frame.set("width", width);
    if(existingLayout.height !== 'auto') {
      frame.set("height", height);
    }

    let layout = frame.get();

    if(ignoreHeightWidgets.includes(type)){
      layout.height = "auto";
    }

    if (fullWidthWidgets.includes(type) &&
      !["100%", "375px"].includes(existingLayout.width) &&
      !["100%", "375px"].includes(recommendation.width)
    ) {
      layout.width = "375px";
    }

    if(type === TEXT) {
      const value = { layout };
      const fs = recommendation.font_size;
      const fontSize =
        typeof fs === "object" && fs !== null
          ? parseInt(Object.values(fs)[0], 10)
          : typeof fs === "string"
          ? parseInt(fs, 10)
          : fs;

      if (fontSize) value.fontSize = fontSize;
      if (recommendation.text_align) {
        value.alignment = recommendation.text_align;
      }

      layoutRequest[uid] = {
        id: uid,
        path: "properties.mobile",
        value,
      }; 
    }
    else if (type === MENU && hasChildAsMenuContainer(uid, widgets)) {
      const value = { layout };
      value.alignment = {position: 'right', margin: 15};

      layoutRequest[uid] = {
        id: uid,
        path: "properties.mobile",
        value
      };

    }
    else {
      layoutRequest[uid] = {
        id: uid,
        path: "properties.mobile.layout",
        value: layout,
      };
    }
  };

  return layoutRequest;
}

export function generateLayoutChanges(htmlString) {
  const doc = parseHtmlToDocument(htmlString);
  const widgetElements = doc.querySelectorAll("[data-widgetid]");
  const layoutRequest = {};

  widgetElements.forEach(el => {
    const uid = el.getAttribute("data-widgetid");
    const type = el.getAttribute("data-widgettype");
    const parent = el.getAttribute("data-widgetparent");
    const styles = parseInlineStyles(el.getAttribute("style"));

    if (type === CONTENT_CARD) {
      contentCardUids.push(uid);
    }

    if (ignoreTypes.includes(type) || contentCardUids.includes(parent)) {
      return;
    }

    const layout = generateLayoutProperties(styles);
    const hasLayout = Object.keys(layout).length > 0;

    if (type === TEXT) {
      const textStyles = extractTextStyles(el);

      const value = {
        fontSize: textStyles.fontSize || 16,
        alignment: textStyles.alignment || "center",
        letterSpacing: styles.letterSpacing,
        lineSpacing: styles.lineHeight,
      };

      if (hasLayout) {
        value.layout = layout;
      }

      layoutRequest[uid] = {
        id: uid,
        path: "properties.mobile",
        value,
      };
    } else {
      if (!hasLayout) {
        return;
      }

      layoutRequest[uid] = {
        id: uid,
        path: "properties.mobile.layout",
        value: layout,
      };
    }
  });

  return layoutRequest;
}

function extractTextStyles(widgetEl) {
  const allEls = widgetEl.querySelectorAll("*");

  let smallestEl = null;
  let smallestFontSize = 16;

  allEls.forEach((el) => {
    if (!el.textContent || !el.textContent.trim()) return;

    const inlineStyles = parseInlineStyles(el.getAttribute("style") || "");
    const fontSize =
      inlineStyles.fontSize ||
      window.getComputedStyle(el).fontSize;

    if (!fontSize) return;

    const size = parseFloat(fontSize);
    if (isNaN(size)) return;

    if (size < smallestFontSize) {
      smallestFontSize = size;
      smallestEl = el;
    }
  });

  if (!smallestEl) return {};

  const inline = parseInlineStyles(
    smallestEl.getAttribute("style") || ""
  );
  const computed = window.getComputedStyle(smallestEl);

  return {
    fontSize: smallestFontSize,
    alignment: inline.textAlign || computed.textAlign,
    letterSpacing: inline.letterSpacing || computed.letterSpacing,
    lineSpacing: inline.lineHeight || computed.lineHeight,
  };
}

function calculateLeftOffsetForMenuContainer(widget) {
  const existingLayout = _.get(widget, 'properties.mobile.layout');
  const [existingX, existingY] =
      existingLayout?.transform?.translate
        ?.split(",")
        .map(v => v.trim()) || [];
  const menuContainerWidth = parseInt(existingLayout.width, 0);
  const leftOffset = mobileWidth - menuContainerWidth - 20;
  const layout = {
    ...existingLayout,
    transform: {
      ...existingLayout.transform,
      translate: `${leftOffset}px,${existingY}`
    }
  }
  return layout;
}

function hasChildAsMenuContainer(id, widgets) {
  return widgets.some(
    item => item.type === MENU_CONTAINER && item.parent === id
  );
}